import React, { useState, useEffect } from "react";
import {
  MenuItem,
  TextField,
  Select,
  Typography,
  ListSubheader,
  Divider,
  Box,
} from "@mui/material";
import { CustomMenuDropdownProps } from "shared/models/custom-menu-dropdown";
import CustomCheckIconButton from "shared/components/CustomCheckIconButton";

const style = {
  "& .MuiOutlinedInput-root": {
    height: "40px",
    borderRadius: 0,
    paddingLeft: 0,
    "&.Mui-focused fieldset": {
      borderColor: "primary",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "8px 12px",
    borderRadius: 0,
    "&::placeholder": {
      color: "text.disabled",
    },
  },
  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
};

const getActiveDotStyles = (isActive: boolean) =>
  isActive
    ? {
        content: '""',
        position: "absolute",
        left: "16px",
        top: "50%",
        transform: "translateY(-50%)",
        width: "8px",
        height: "8px",
        backgroundColor: "primary.main",
        borderRadius: "50%",
      }
    : {};

export default function CustomDropdown({
  defaultValue,
  presetValues,
  customHeader,
  customTextFieldPlaceholder = "",
  showPresetMenuList,
  showCustomTextField,
  presetHeader,
  handleClick,
  isNumberType = true,
  allowNegativeCustomValue,
  allowMathSymbols,
  unitOptions,
  selectedUnit,
  setSelectedUnit,
  activeValue = "",
  isCustomValueActive = false,
  value = "",
}: CustomMenuDropdownProps): JSX.Element {
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [customValue, setCustomValue] = useState(value);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (value) {
      setCustomValue(value);
    }
  }, [value]);

  const isRateMenu = presetValues?.some(
    (value) =>
      value.value.includes("%") ||
      value.label.includes("slow") ||
      value.label.includes("fast")
  );

  const handlePresetSelect = (value: string, label: string) => {
    setSelectedValue(`${label} (${value})`);
    setCustomValue("");
    handleClick?.(value);
    setOpen(false);
  };

  const handleCustomValueSubmit = () => {
    if (
      !customValue.trim() ||
      (!allowNegativeCustomValue && parseFloat(customValue) <= 0)
    )
      return;
    const displayValue = `Custom (${customValue})`;
    setSelectedValue(displayValue);
    handleClick?.(customValue);
    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      !allowMathSymbols &&
      !allowNegativeCustomValue &&
      ["+", "e", "E"].includes(event.key)
    ) {
      event.preventDefault();
    }
  };

  const menuItems = [];

  if (showPresetMenuList && presetValues) {
    menuItems.push(
      <ListSubheader
        key="preset-header"
        sx={{ px: 2, py: 1, backgroundColor: "background.paper" }}
      >
        <Typography variant="subtitle2">{presetHeader}</Typography>
      </ListSubheader>,
      ...presetValues.map((presetValue) => {
        const isActive = activeValue === presetValue.value;
        return (
          <MenuItem
            data-testid={`preset-item-${presetValue.value}`}
            key={presetValue.value}
            value={`${presetValue.label} (${presetValue.value})`}
            onClick={() =>
              handlePresetSelect(presetValue.value, presetValue.label)
            }
            sx={{
              position: "relative",
              pl: isActive ? "36px" : "16px",
              "&::before": getActiveDotStyles(isActive),
            }}
          >
            {`${presetValue.label} (${presetValue.value})`}
          </MenuItem>
        );
      }),
      <Divider key="divider" sx={{ my: 1 }} />
    );
  }

  if (showCustomTextField) {
    const isCustomActive = !!(isCustomValueActive && activeValue);

    menuItems.push(
      <ListSubheader
        key="custom-header"
        sx={{
          px: 2,
          py: 1,
          position: "relative",
          pl: isCustomActive ? "36px" : "16px",
          backgroundColor: "background.paper",
          "&::before": getActiveDotStyles(isCustomActive),
        }}
      >
        <Typography variant="subtitle2">{customHeader}</Typography>
      </ListSubheader>,
      <MenuItem
        key="custom-input"
        disableRipple
        sx={{
          padding: "8px 16px",
          backgroundColor: "transparent !important",
          "&:hover": { backgroundColor: "transparent !important" },
          "&.Mui-focusVisible": { backgroundColor: "transparent !important" },
          "&.Mui-selected": { backgroundColor: "transparent !important" },
          "& .MuiTouchRipple-root": { display: "none" },
        }}
      >
        <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
          {!isRateMenu && unitOptions && unitOptions.length > 0 && (
            <Select
              value={selectedUnit || ""}
              onChange={(e) => setSelectedUnit?.(e.target.value)}
              size="small"
              onClick={(e) => e.stopPropagation()}
              sx={{ width: "30%" }}
            >
              {unitOptions.map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {unit}
                </MenuItem>
              ))}
            </Select>
          )}
          <TextField
            fullWidth
            placeholder={customTextFieldPlaceholder}
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            onKeyDown={handleKeyDown}
            type={isNumberType ? "number" : "text"}
            size="small"
            onClick={(e) => e.stopPropagation()}
            sx={{
              ...style,
              "& .MuiOutlinedInput-root": {
                ...style["& .MuiOutlinedInput-root"],
                ...(isCustomActive && {
                  "& fieldset": {
                    borderColor: "primary.main",
                  },
                }),
              },
            }}
          />
          <CustomCheckIconButton
            handleSubmit={handleCustomValueSubmit}
            currentInputValue={customValue}
          />
        </Box>
      </MenuItem>
    );
  }

  return (
    <Select
      data-testid="custom-dropdown"
      value={selectedValue}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      displayEmpty
      fullWidth
      onChange={() => {}}
      renderValue={() => (
        <Typography variant="body1" fontWeight="400">
          {selectedValue}
        </Typography>
      )}
      sx={{
        height: "40px",
        textTransform: "none",
        color: "text.primary",
        marginTop: "8px",
      }}
    >
      {menuItems}
    </Select>
  );
}
